import './bank-offers.scss';

import { Alert } from 'antd';
import React from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { BankNames } from '@/constants';
import { useAppSelector } from '@/store';

import { BankWaitingItem } from '../../entities';
import {
  AltynApprovedItem,
  AltynDeclinedItem,
  BccApprovedItem,
  BccDeclinedItem,
  BerekeApprovedItem,
  BerekeDeclinedItem,
  EurasApprovedItem,
  EurasDeclinedItem,
  EurasV2ApprovedItem,
  EurasV2DeclinedItem,
  EurasWaitingItem,
  ForteApprovedItem,
  ForteDeclinedItem,
  ForteWaitingItem,
  FreedomApprovedItem,
  FreedomDeclinedItem,
  HalykApprovedItem,
  HalykDeclinedItem,
  KmfApprovedItem,
  KmfDeclinedItem,
  MfoApprovedItem,
  MfoDeclinedItem,
  SapaApprovedItem,
  SapaDeclinedItem,
  ShinhanApprovedItem,
  ShinhanDeclinedItem,
  SolvaApprovedItem,
  SolvaDeclinedItem
} from '../../features';
import type { TBankOffers } from '../../types/bank-offer.types';
import type { TBccBankOffer } from '../../types/bcc.types';
import type { TEurasV1BankOffer } from '../../types/euras-v1.types';
import type { TEurasV2BankOffer } from '../../types/euras-v2.types';
import { BankOffersError } from '../../ui';

type TProps = {
  approvedOffers: TBankOffers[];
  waitingOffers: TBankOffers[];
  declinedOffers: TBankOffers[];
};

const ApprovedBankOffersMap: Partial<
  Record<
    BankNames,
    (props: {
      bankOffer: TBankOffers;
      hasOfferedResponse: boolean;
    }) => React.ReactNode
  >
> = {
  [BankNames.altyn]: (props) => <AltynApprovedItem {...props} />,
  [BankNames.bcc]: (props) => (
    <BccApprovedItem
      {...(props as { bankOffer: TBccBankOffer; hasOfferedResponse: boolean })}
    />
  ),
  [BankNames.bereke]: (props) => <BerekeApprovedItem {...props} />,
  [BankNames.euBank]: (props) => (
    <EurasApprovedItem
      {...(props as {
        bankOffer: TEurasV1BankOffer;
        hasOfferedResponse: boolean;
      })}
    />
  ),
  [BankNames.euBankV2]: (props) => (
    <EurasV2ApprovedItem
      {...(props as {
        bankOffer: TEurasV2BankOffer;
        hasOfferedResponse: boolean;
      })}
    />
  ),
  [BankNames.ffb]: (props) => <FreedomApprovedItem {...props} />,
  [BankNames.forte]: (props) => <ForteApprovedItem {...props} />,
  [BankNames.halyk]: (props) => <HalykApprovedItem {...props} />,
  [BankNames.kmf]: (props) => <KmfApprovedItem {...props} />,
  [BankNames.mfo]: (props) => <MfoApprovedItem {...props} />,
  [BankNames.sapa]: (props) => <SapaApprovedItem {...props} />,
  [BankNames.shinhan]: (props) => <ShinhanApprovedItem {...props} />,
  [BankNames.solva]: (props) => <SolvaApprovedItem {...props} />
};

const WaitingBankOffersMap: Partial<
  Record<BankNames, (props: { bankOffer: TBankOffers }) => React.ReactNode>
> = {
  [BankNames.euBank]: (props) => (
    <EurasWaitingItem {...(props as { bankOffer: TEurasV1BankOffer })} />
  ),
  [BankNames.forte]: (props) => <ForteWaitingItem {...props} />
};

const DeclinedBankOffersMap: Partial<
  Record<BankNames, (props: { bankOffer: TBankOffers }) => React.ReactNode>
> = {
  [BankNames.altyn]: (props) => <AltynDeclinedItem {...props} />,
  [BankNames.bcc]: (props) => (
    <BccDeclinedItem {...(props as { bankOffer: TBccBankOffer })} />
  ),
  [BankNames.bereke]: (props) => <BerekeDeclinedItem {...props} />,
  [BankNames.euBank]: (props) => (
    <EurasDeclinedItem {...(props as { bankOffer: TEurasV1BankOffer })} />
  ),
  [BankNames.euBankV2]: (props) => (
    <EurasV2DeclinedItem {...(props as { bankOffer: TEurasV2BankOffer })} />
  ),
  [BankNames.ffb]: (props) => <FreedomDeclinedItem {...props} />,
  [BankNames.forte]: (props) => <ForteDeclinedItem {...props} />,
  [BankNames.halyk]: (props) => <HalykDeclinedItem {...props} />,
  [BankNames.kmf]: (props) => <KmfDeclinedItem {...props} />,
  [BankNames.mfo]: (props) => <MfoDeclinedItem {...props} />,
  [BankNames.sapa]: (props) => <SapaDeclinedItem {...props} />,
  [BankNames.shinhan]: (props) => <ShinhanDeclinedItem {...props} />,
  [BankNames.solva]: (props) => <SolvaDeclinedItem {...props} />
};

const BankOffers = ({
  approvedOffers,
  waitingOffers,
  declinedOffers
}: TProps) => {
  const { banksOffers } = useAppSelector((state) => state.offerStep);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { selectedLeasing } = useAppSelector((state) => state.financingStep);

  const hasOfferedResponse: boolean =
    !!selectedLeasing ||
    banksOffers.find((item) => item.isOffered) !== undefined;

  const hasClientAccepted: boolean = banksOffers.some(
    (offer) => offer.status.code === 'client_accepted'
  );

  const onlyErrorResponses: boolean =
    declinedOffers.length > 0 &&
    approvedOffers.length === 0 &&
    waitingOffers.length === 0;

  return (
    <div key="bank-offers" id="bank-offers" className="bank-offers">
      {!hasOfferedResponse &&
        !hasClientAccepted &&
        !onlyErrorResponses &&
        orderApp?.isOnlineRequest && (
          <Alert
            className="bank-offers__warning"
            title="Ожидание выбора клиента"
            description="Клиент не выбрал ни одного из доступных предложений"
            type="warning"
            showIcon
          />
        )}

      {approvedOffers.length === 0 &&
        waitingOffers.length === 0 &&
        declinedOffers.length === 0 && (
          <div className="bank-offers__empty">Нет предложений</div>
        )}

      {approvedOffers.length > 0 &&
        approvedOffers.map((bankOffer) => {
          if (!bankOffer) return null;
          const ApprovedBankOffer = ApprovedBankOffersMap[bankOffer.bank.name];

          return (
            <ErrorBoundary
              FallbackComponent={(props) => (
                <BankOffersError
                  {...props}
                  bank={bankOffer.bank.name}
                  extId={bankOffer.extId || ''}
                  requestBodyInfo={{
                    bankName: bankOffer.bank.name,
                    responsePrimaryBody: bankOffer.responsePrimaryBody,
                    responseSendedBody: bankOffer.responseSendedBody
                  }}
                />
              )}
              key={bankOffer.offerId + bankOffer.extId + bankOffer.id}
            >
              {ApprovedBankOffer ? (
                <ApprovedBankOffer
                  bankOffer={bankOffer}
                  hasOfferedResponse={hasOfferedResponse}
                />
              ) : null}
            </ErrorBoundary>
          );
        })}
      {waitingOffers.length > 0 &&
        waitingOffers.map((bankOffer) => {
          const WaitingBankOffer = WaitingBankOffersMap[bankOffer.bank.name];
          return WaitingBankOffer ? (
            <WaitingBankOffer
              bankOffer={bankOffer}
              key={bankOffer.offerId + bankOffer.extId + bankOffer.id}
            />
          ) : (
            <BankWaitingItem
              bankOffer={bankOffer}
              key={bankOffer.offerId + bankOffer.extId + bankOffer.id}
            />
          );
        })}
      {declinedOffers.length > 0 && (
        <div>
          {!onlyErrorResponses && (
            <h1 className="bank-offers__declined-title"></h1>
          )}
          {declinedOffers.map((bankOffer) => {
            const DeclinedBankOffer =
              DeclinedBankOffersMap[bankOffer.bank.name];
            return DeclinedBankOffer ? (
              <DeclinedBankOffer
                bankOffer={bankOffer}
                key={bankOffer.offerId + bankOffer.extId + bankOffer.id}
              />
            ) : null;
          })}
        </div>
      )}
    </div>
  );
};

export default BankOffers;
